import ENV from "@/utils/env";
import { api } from "@/lib/api";
import { AssetInsight, SceneInsight, VideoInsight } from "./types/insight.type";
import { VideoStudySummary } from "./types/study-summary.type";

export interface GetAllInsightsReq {
  projectId: string;
  studyId: string;
  sortBy?: "asc" | "desc";
}
export interface GetAllAssetInsightsReq extends GetAllInsightsReq {
  orderBy?: "attention" | "clarity" | "digestibility" | "visibility" | "memory" | "emotion" | "creative" | "name";
}

export interface GetAllSceneInsightsReq extends GetAllInsightsReq {
  orderBy?: "vis" | "pop" | "soa" | "pow";
}

export interface GetAllVideoInsightsReq extends GetAllInsightsReq {
  orderBy?: "creative" | "name";
}

/*
 * getAllAssetInsights endpoint
 *
 * This endpoint is used to get all insights for every asset in a study.
 * It will only have insights data if it has been processed in the pipeline.
 */
export const getAllAssetInsights = async ({ projectId, studyId, sortBy, orderBy }: GetAllAssetInsightsReq) => {
  const resp = await api.get(`${ENV.API_PLATFORM_URL}/v3/studio/projects/${projectId}/studies/${studyId}/assets/insights`, {
    params: {
      sortBy,
      orderBy,
    },
  });
  return resp.data as AssetInsight[];
};

/*
 * getAllSceneInsights endpoint
 *
 * This endpoint is used to get all insights for every scene in a study.
 * It will only have insights data if it has been processed in the pipeline.
 */
export const getAllSceneInsights = async ({ projectId, studyId, sortBy, orderBy }: GetAllSceneInsightsReq) => {
  const resp = await api.get(`${ENV.API_PLATFORM_URL}/v3/studio/projects/${projectId}/studies/${studyId}/scenes/insights`, {
    params: {
      sortBy,
      orderBy,
    },
  });
  return resp.data as SceneInsight[];
};

/*
 * getAllVideoInsights endpoint
 *
 * This endpoint is used to get all insights for every video in a study.
 * It will only have insights data if it has been processed in the pipeline.
 */
export const getAllVideoInsights = async ({ projectId, studyId, sortBy, orderBy }: GetAllVideoInsightsReq) => {
  const resp = await api.get(`${ENV.API_PLATFORM_URL}/v3/studio/projects/${projectId}/studies/${studyId}/videos/insights`, {
    params: {
      sortBy,
      orderBy,
    },
  });
  return resp.data as VideoInsight[];
};

export interface GetVideoStudySummaryReq {
  projectId: string;
  studyId: string;
}

// TODO(DFP-1121): Replace this mock with a real call once the pipeline returns a generated study-level summary.
const MOCK_VIDEO_STUDY_SUMMARY: VideoStudySummary = {
  summary:
    "Only Kit Kat 1 exceeds the benchmark - it holds attention once the ad is running, while Kit Kat 2 and 3 stay flat. All three struggle to grab attention at the beginning.",
};

/*
 * getVideoStudySummary endpoint
 *
 * Mocked until the pipeline returns a generated study-level Copilot summary for a video study.
 * This is study-level, not per-asset, so it is kept separate from VideoInsight rather than
 * added as a field on it.
 */
export const getVideoStudySummary = async (req: GetVideoStudySummaryReq): Promise<VideoStudySummary> => {
  void req; // projectId/studyId will become URL params once this calls the real endpoint
  return Promise.resolve(MOCK_VIDEO_STUDY_SUMMARY);
};
